/* PlayFaire Gift Quiz — version 2.0 / safe collection routing
 * Works with gift-quiz.liquid from this package.
 * Keeps all existing question answers and their data-tag values unchanged.
 * Checks collections instead of assuming 216 pre-created combinations exist.
 * If the match is missing or known empty, offers age-first alternatives.
 */
(function () {
  function initialiseGiftQuiz() {
    const root = document.getElementById('quiz-container');
    if (!root || root.dataset.pfQuizReady === 'true') return;
    root.dataset.pfQuizReady = 'true';

    const questions = Array.from(root.querySelectorAll('.question'));
    const progressBar = root.querySelector('#progress-bar');
    const status = root.querySelector('#pf-quiz-status');
    const alternatives = root.querySelector('#pf-quiz-alternatives');
    const altDescription = root.querySelector('#pf-quiz-alt-description');
    const ageLink = root.querySelector('#pf-quiz-age-link');
    const categoryLink = root.querySelector('#pf-quiz-category-link');
    const changeAnswers = root.querySelector('#pf-quiz-change-answers');
    if (questions.length !== 3 || !alternatives) return;

    let currentStep = 0;
    let busy = false;
    const selections = {};
    const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const rootPath = shopRoot.endsWith('/') ? shopRoot : shopRoot + '/';

    // Exact quiz values from the user-supplied Liquid.
    const ageMap = {
      '0+': { handle: '0plus', label: '0–1 year', broad: 'baby-play-activity' },
      '1+': { handle: '1plus', label: '1–2 years', broad: 'wooden-toys-for-1-year-olds' },
      '2+': { handle: '2plus', label: '2–3 years', broad: '2years' },
      '3+': { handle: '3plus', label: '3–4 years', broad: '3years' },
      '4+': { handle: '4plus', label: '4–5 years', broad: '4years' },
      '5+': { handle: '5plus', label: '5–8 years', broad: '5years' }
    };
    const budgetMap = {
      '£15':   { handle: '15', label: 'under £15', min: 0, max: 15 },
      '£25':   { handle: '25', label: '£15–£25', min: 15, max: 25 },
      '£50':   { handle: '50', label: '£25–£50', min: 25, max: 50 },
      '£75':   { handle: '75', label: '£50–£75', min: 50, max: 75 },
      '£100':  { handle: '100', label: '£75–£100', min: 75, max: 100 },
      Hundred: { handle: 'hundred', label: '£100+', min: 100, max: null }
    };
    const categoryMap = {
      'Role Play': { handle: 'role-play', label: 'role play', broad: 'role-play-toys' },
      Activity: { handle: 'activity', label: 'activity toys', broad: 'activity-toys' },
      Dolls: { handle: 'dolls', label: 'dolls', broad: 'dolls' },
      Vehicles: { handle: 'vehicles', label: 'vehicles', broad: 'cars-planes-and-transport' },
      'Games & Puzzles': { handle: 'games-puzzles', label: 'games and puzzles', broad: 'games-puzzles' },
      Educational: { handle: 'educational', label: 'educational toys', broad: 'educational-toys' }
    };

    const collectionURL = (handle) => `${rootPath}collections/${encodeURIComponent(handle)}`;
    function priceQuery(budget) {
      const params = new URLSearchParams();
      if (budget.min) params.set('filter.v.price.gte', String(budget.min));
      if (budget.max !== null) params.set('filter.v.price.lte', String(budget.max));
      return params.toString();
    }
    function withBudget(url, budget) {
      const query = priceQuery(budget);
      return query ? `${url}?${query}` : url;
    }
    function updateProgressBar() {
      if (progressBar) progressBar.style.width = `${(currentStep / 2) * 100}%`;
    }
    function updateBackButtons() {
      questions.forEach((question, index) => {
        question.querySelectorAll('.back-button').forEach((button) => {
          button.hidden = busy || currentStep === 0 || index !== currentStep;
        });
      });
    }
    function showQuestion(step) {
      currentStep = Math.max(0, Math.min(2, step));
      questions.forEach((q, index) => q.classList.toggle('active', index === currentStep));
      alternatives.hidden = true;
      status.hidden = true;
      updateProgressBar();
      updateBackButtons();
    }
    function setBusy(state) {
      busy = state;
      status.hidden = !state;
      root.setAttribute('aria-busy', String(state));
      root.querySelectorAll('.answer').forEach((button) => { button.disabled = state; });
      updateBackButtons();
    }

    async function fetchWithTimeout(url, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 9000);
      try {
        return await fetch(url, {
          credentials: 'same-origin',
          signal: controller.signal,
          ...options
        });
      } finally {
        clearTimeout(timeout);
      }
    }

    function variants(product) {
      return Array.isArray(product.variants) ? product.variants : [];
    }
    function moneyToGBP(value) {
      if (value === undefined || value === null) return NaN;
      // Shopify collection products.json typically provides strings in major units.
      const parsed = Number(String(value).replace(/[^0-9.\-]/g, ''));
      return Number.isFinite(parsed) ? parsed : NaN;
    }
    function withinBudget(amount, budget) {
      if (!Number.isFinite(amount)) return false;
      return amount >= budget.min && (budget.max === null || amount <= budget.max);
    }

    /*
     * Returns false only for definitive empty/unavailable cases.
     * Returns true for visible products or when variant availability cannot be verified.
     * An unavailable JSON endpoint is not evidence of an empty collection.
     */
    async function collectionHasPossibleMatches(handle, budget) {
      const pageURL = collectionURL(handle);
      const pageResponse = await fetchWithTimeout(pageURL, { cache: 'no-store' });
      if (!pageResponse.ok) return false; // Exact URL missing, e.g. 404.

      let data = null;
      try {
        const jsonResponse = await fetchWithTimeout(`${pageURL}/products.json?limit=250`, { cache: 'no-store' });
        if (jsonResponse.ok) {
          const result = await jsonResponse.json();
          if (result && Array.isArray(result.products)) data = result.products;
        }
      } catch (error) {
        console.info('[PlayFaire Gift Quiz] Product JSON unavailable; using the collection page.', error);
      }

      if (data !== null) {
        if (!data.length) return false;
        let confirmedAvailable = 0;
        let unknownAvailability = 0;
        let observedAvailability = false;
        let confirmedPrice = 0;
        let observedPrice = false;

        for (const product of data) {
          const vs = variants(product);
          const items = vs.length ? vs : [product];
          for (const v of items) {
            const availability = typeof v.available === 'boolean' ? v.available :
              (typeof product.available === 'boolean' ? product.available : null);
            if (availability !== null) observedAvailability = true;
            if (availability === false) continue;
            if (availability === null) unknownAvailability++;
            else confirmedAvailable++;
            const price = moneyToGBP(v.price === undefined ? product.price : v.price);
            if (Number.isFinite(price)) {
              observedPrice = true;
              if (withinBudget(price, budget)) confirmedPrice++;
            }
          }
        }
        if (observedAvailability && !confirmedAvailable && !unknownAvailability) return false;
        if (observedPrice && !confirmedPrice) return false;
        return true;
      }

      // If Shopify disables products.json, inspect the visible collection HTML.
      const html = await pageResponse.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = parsed.querySelectorAll('.collection-listing .product-block, .product-list .product-block');
      if (cards.length > 0) {
        const unsold = Array.from(cards).some((el) => {
          const text = (el.textContent || '').toLowerCase();
          return !/sold out|out of stock/.test(text);
        });
        return unsold;
      }
      // Unknown theme markup: existing URL is valid, so don't falsely declare zero stock.
      return true;
    }

    async function collectionExists(handle) {
      try {
        const response = await fetchWithTimeout(collectionURL(handle));
        return response.ok;
      } catch (error) {
        console.warn('[PlayFaire Gift Quiz] Could not validate alternative collection:', handle, error);
        return false;
      }
    }

    async function showAlternatives(age, budget, category) {
      questions.forEach((q) => q.classList.remove('active'));
      alternatives.hidden = false;
      status.hidden = true;
      updateBackButtons();

      altDescription.textContent =
        `We couldn't confirm an exact match for ${category.label} and ages ${age.label} ` +
        `within ${budget.label}. Here are some other lovely ways to find a gift.`;

      // Hide links until each is confirmed to exist; never send customers to another 404.
      ageLink.hidden = true;
      categoryLink.hidden = true;
      const [ageAvailable, categoryAvailable] = await Promise.all([
        collectionExists(age.broad),
        collectionExists(category.broad)
      ]);

      if (ageAvailable) {
        ageLink.href = withBudget(collectionURL(age.broad), budget);
        ageLink.textContent = `Other gifts for ages ${age.label} →`;
        ageLink.hidden = false;
      }
      if (categoryAvailable) {
        categoryLink.href = withBudget(collectionURL(category.broad), budget);
        categoryLink.textContent = `Browse more ${category.label} →`;
        categoryLink.hidden = false;
      }
      if (!ageAvailable && !categoryAvailable) {
        ageLink.href = `${rootPath}collections/all`;
        ageLink.textContent = 'Browse all toys →';
        ageLink.hidden = false;
        altDescription.textContent += ' You can also change your answers to try another combination.';
      }
      alternatives.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    }

    async function redirectToResults() {
      const age = ageMap[selections['0']];
      const budget = budgetMap[selections['1']];
      const category = categoryMap[selections['2']];
      if (!age || !budget || !category) {
        console.error('[PlayFaire Gift Quiz] Missing or unrecognised answer', selections);
        status.textContent = 'Please go back and choose an option for each question.';
        status.hidden = false;
        return;
      }
      setBusy(true);
      const handle = `${age.handle}-${budget.handle}-${category.handle}`;
      console.info('[PlayFaire Gift Quiz] Checking:', handle);
      let exactMatch = false;
      try {
        exactMatch = await collectionHasPossibleMatches(handle, budget);
      } catch (error) {
        console.warn('[PlayFaire Gift Quiz] Exact collection check failed', error);
      }
      if (exactMatch) {
        window.location.assign(collectionURL(handle));
        return;
      }
      setBusy(false);
      await showAlternatives(age, budget, category);
    }

    root.querySelectorAll('.answer').forEach((button) => {
      button.addEventListener('click', async function () {
        if (busy) return;
        const question = button.closest('.question');
        const index = questions.indexOf(question);
        if (index < 0) return;
        selections[String(index)] = button.getAttribute('data-tag');
        if (index < 2) showQuestion(index + 1);
        else await redirectToResults();
      });
    });
    root.querySelectorAll('.back-button').forEach((button) => {
      button.addEventListener('click', () => { if (!busy && currentStep > 0) showQuestion(currentStep - 1); });
    });
    changeAnswers.addEventListener('click', () => {
      if (!busy) {
        delete selections['2'];
        showQuestion(2); // Let customers change toy type, or use Back to change budget/age.
        questions[2].querySelector('h2')?.focus({ preventScroll: true });
      }
    });
    showQuestion(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseGiftQuiz, { once: true });
  } else {
    initialiseGiftQuiz();
  }
})();